import { useMutation, useQuery } from 'react-query';
import {
  IQuestionnaireProps,
  TAnswer,
} from '../components/Questionnaire/models';
import { BASE_URL, ERROR_MESSAGE } from 'constants/index';

type TUseFetch = {
  setSummary: IQuestionnaireProps['onSaveSummary'];
  sessionKey: string;
};

export const useFetch = ({ setSummary, sessionKey }: TUseFetch) => {
  const {
    data: questions,
    isError: isQuestionsError,
    isLoading: isLoadingQuestions,
  } = useQuery(
    'Questions',
    async () => {
      const response = await fetch(`${BASE_URL}/get_initial_questions`, 
        {
          headers: {
            'X-Session-ID': `${sessionKey}`,
          },
        }
      );
      if (!response.ok) throw new Error(ERROR_MESSAGE);
      return response.json();
    },
    {
      onError: (e) => {
        console.error('Error fetching questions:', e);
      },
    },
  );

  const {
    data: additionalQuestion,
    isError: isAdditionalQuestionsError,
    isLoading: isLoadingAdditionalQuestions,
    refetch: fetchMoreQuestions,
  } = useQuery(
    'MoreQuestions',
    async () => {
      const response = await fetch(`${BASE_URL}/get_additional_questions`,{
        headers: {
          'X-Session-ID': `${sessionKey}`,
        },
      });
      if (!response.ok) throw new Error(ERROR_MESSAGE);
      return response.json();
    },
    {
      enabled: false,
      onError: (e) => {
        console.error('Error fetching more questions:', e);
      },
    },
  );

  const {
    mutate: submitAnswersMutation,
    isLoading: isLoadingSubmitAnswers,
    isError: isSubmitAnswersError,
  } = useMutation<{ summary: string }, Error, TAnswer[]>({
    mutationFn: async (data) => {
      const response = await fetch(`${BASE_URL}/submit_answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': `${sessionKey}`,
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error(ERROR_MESSAGE);
      return response.json();
    },
    onSuccess: ({ summary }) => {
      setSummary(summary);
    },
    onError: (e) => {
      console.error('Error adding question:', e);
    },
  });

  return {
    questions,
    isLoadingQuestions,
    isQuestionsError,

    additionalQuestion,
    isAdditionalQuestionsError,
    isLoadingAdditionalQuestions,
    fetchMoreQuestions,

    submitAnswersMutation,
    isLoadingSubmitAnswers,
    isSubmitAnswersError,
  };
};
